import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {
  get,
} from 'lodash';
import {
  TimeLabel,
  Field2 as Field,
  LoopCard,
  MessageModel,
  VibeButton,
  VibeIcon,
  viClose,
  color,
} from 'vibeguide';
import {
  Popover,
} from '@mui/material';
import ArchivedBanner from './ArchivedBanner';
import './Loop.scss';

class Loop extends PureComponent {
  constructor(props) {
    super(props);

    const {
      startTime,
      stopTime,
    } = props;

    this.itemRef = React.createRef();

    this.state = {
      showDetails: false,
      startTimeBeforeSave: startTime,
      stopTimeBeforeSave: stopTime,
      invalidStartTime: false,
      invalidStopTime: false,
    };
  }

  componentDidUpdate(prevProps) {
    const {
      startTime,
      stopTime,
    } = this.props;

    const {
      startTime: prevStartTime,
      stopTime: prevStopTime,
    } = prevProps;

    if (startTime !== prevStartTime || stopTime !== prevStopTime) {
      this.setState({
        startTimeBeforeSave: startTime,
        stopTimeBeforeSave: stopTime,
      });
    }
  }

  onRemove = () => {
    const {
      id,
    } = this.props;

    document.dispatchEvent(new CustomEvent('onRemoveItem', {
      detail: {
        type: 'Loop',
        _uniqueId: id,
      },
    }));
  };

  onChangeStartTime = (data) => {
    this.setState({
      startTimeBeforeSave: data.time24,
      invalidStartTime: data.hasError,
    });
  };

  onChangeStopTime = (data) => {
    this.setState({
      stopTimeBeforeSave: data.time24,
      invalidStopTime: data.hasError,
    });
  };

  onClosePopover = () => {
    this.setState({
      showDetails: false,
    });
  };

  onSave = () => {
    const {
      id,
    } = this.props;

    const {
      startTimeBeforeSave,
      stopTimeBeforeSave,
    } = this.state;

    document.dispatchEvent(new CustomEvent('onChangeItemDuration', {
      detail: {
        type: 'Loop',
        startTime: startTimeBeforeSave,
        stopTime: stopTimeBeforeSave,
        _uniqueId: id,
      },
    }));

    this.onClosePopover();
  };

  toggleActive = () => {
    this.setState((state) => {
      return {
        showDetails: !state.showDetails,
      };
    });
  };

  render() {
    const {
      loop,
      startTime,
      stopTime,
      minStartTime,
      maxStopTime,
      isHover,
      locationId,
      companyId,
      archived,
      day,
      readOnly,
    } = this.props;

    const {
      showDetails,
      startTimeBeforeSave,
      stopTimeBeforeSave,
      invalidStartTime,
      invalidStopTime,
    } = this.state;

    const {
      itemRef: {
        current: itemRef,
      },
    } = this;

    const messages = get(loop, 'messages', []);

    if (messages.length > 0 && !messages[0].expired) {
      for (let y = 0; y < messages.length; y++) {
        const message = messages[y];
        messages[y] = new MessageModel(message);
      }
    }

    if (isHover) {
      return (
        <div className="Loop is-hover">
          {archived && (
            <ArchivedBanner />
          )}

          <div className="item-container">
            <div className="loop-span">
              <TimeLabel
                time={startTime}
                showTooltip
              />
              -
              <TimeLabel
                time={stopTime}
                showTooltip
                midnightNextDay
              />
            </div>

            <div className="loop-info">
              <LoopCard
                loop={loop}
                locationId={locationId}
                companyId={companyId}
                width="auto"
                height={373}
                day={day}
                withoutCardStyle
              />
            </div>
          </div>
        </div>
      );
    }

    const itemRefRect = itemRef
      ? itemRef.getBoundingClientRect()
      : { top: 0, left: 0 };

    const top = itemRefRect.top - 20;
    const left = itemRefRect.left + (itemRefRect.width / 2);

    return (
      <div className={classNames('Loop', { active: showDetails, archived })}>
        {!readOnly && (
          <VibeIcon
            className="loop-remove"
            icon={viClose}
            color={color.manatee}
            hoverColor={color.obsidian}
            size={16}
            onClick={this.onRemove}
          />
        )}

        <div
          className="item-container"
          ref={this.itemRef}
          onClick={!readOnly
            ? this.toggleActive
            : null}
        >
          <div className="loop-span">
            <TimeLabel
              time={startTime}
              showTooltip
            />
            -
            <TimeLabel
              time={stopTime}
              midnightNextDay
              showTooltip
            />
          </div>

          <div className="loop-info">
            <LoopCard
              loop={loop}
              locationId={locationId}
              companyId={companyId}
              width="auto"
              height="100%"
              day={day}
              withoutCardStyle
            />
          </div>
        </div>

        <Popover
          anchorReference="anchorPosition"
          anchorPosition={{
            top,
            left,
          }}
          open={showDetails}
          onClose={this.onClosePopover}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          classes={{
            root: 'LoopPopperRoot',
            paper: 'LoopPopper',
          }}
        >
          <div className="item-details-container">
            <div className="item-details">
              <div className="details-toolbar">
                <VibeIcon
                  icon={viClose}
                  color={color.manatee}
                  hoverColor={color.obsidian}
                  onClick={!readOnly
                    ? this.toggleActive
                    : null}
                />
              </div>

              <div className="time-fields">
                <div className="start-time">
                  <Field
                    type="time"
                    label="Start Time"
                    value={startTimeBeforeSave}
                    tabIndex={1}
                    timeProps={{
                      // prevent overlapping items in the schedule
                      min: minStartTime,
                      max: stopTime,
                    }}
                    onChange={this.onChangeStartTime}
                  />
                </div>
                <div className="stop-time">
                  <Field
                    type="time"
                    label="Stop Time"
                    value={stopTimeBeforeSave}
                    tabIndex={2}
                    timeProps={{
                      min: startTime,
                      // prevent overlapping items in the schedule
                      max: maxStopTime,
                      midnightNextDay: true,
                    }}
                    onChange={this.onChangeStopTime}
                  />
                </div>
              </div>

              <div
                style={{
                  marginTop: 8,
                  textAlign: 'center',
                }}
              >
                <VibeButton
                  text="Save"
                  color="secondary"
                  disabled={invalidStartTime || invalidStopTime}
                  onClick={this.onSave}
                />
              </div>
            </div>
          </div>
        </Popover>
      </div>
    );
  }
}

Loop.propTypes = {
  /** Unique ID in the TimeSlot */
  id: PropTypes.string,
  loop: PropTypes.object.isRequired,
  startTime: PropTypes.string,
  stopTime: PropTypes.string,
  /** Prevent starting before */
  minStartTime: PropTypes.string,
  /** Prevent stopping after */
  maxStopTime: PropTypes.string,
  locationId: PropTypes.string,
  companyId: PropTypes.string,
  archived: PropTypes.bool,
  /** Day of the week the loop is being shown */
  day: PropTypes.string,
  /** When the item is inside a hover popover */
  isHover: PropTypes.bool,
  /** All data in the schedule is read-only */
  readOnly: PropTypes.bool,
};

Loop.defaultProps = {
  id: null,
  startTime: '',
  stopTime: '',
  minStartTime: '',
  maxStopTime: '',
  locationId: '*',
  companyId: '*',
  archived: false,
  day: null,
  isHover: false,
  readOnly: false,
};

export default Loop;
